import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { readPendingConversationId } from "@/components/screens/Chat/helpers";
import LOCAL_STORAGE from "@/constants/local-storage";
import { initialNotificationFilterValues } from "@/constants/notification.constants";
import {
  defaultPageNumber as pageNumber,
  defaultPageSize as pageSize,
} from "@/constants/pagination";
import SWR_KEYS from "@/constants/swr-keys";
import { checkIfTokenIsExpired, decodeToken, getAccessToken } from "@/helpers/auth-helpers";
import { isCustomerPlatformBindingActive } from "@/helpers/customer-platform-binding.helpers";
import { markUserActivity } from "@/utils/session-activity";
import useGetAllConversations from "@/hooks/requests/message/use-get-all-conversations";
import useUnreadConversationsCount from "@/hooks/requests/message/use-unread-conversations-count";
import useGetNotifications from "@/hooks/requests/notification/use-get-notification";
import useGetAllPlatforms from "@/hooks/requests/platform/use-get-all-platform";
import { usePlatformId } from "@/hooks/requests/platform/use-platform-id";
import useFilters from "@/hooks/use-filters";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import useLoading from "@/hooks/use-loading";
import useMutate from "@/hooks/use-mutate";
import { IChat } from "@/interfaces/chat.interfaces";
import { IConversation } from "@/interfaces/conversation.interfaces";
import { ICustomerPlatform } from "@/interfaces/customer-platform.interfaces";
import { IMessage } from "@/interfaces/message.interface";
import INotification from "@/interfaces/notification.interface";
import authService from "@/services/auth.service";
import conversationService from "@/services/conversation.service";
import customerPlatformService from "@/services/customer-platform.service";
import pushNotificationService from "@/services/push-notification.service";
import { signalRService } from "@/services/signalr.service";
import { isPWAStandalone } from "@/utils/environment.utils";
import {
  AIAgentsFilter,
  ConversationFilter,
  ConversationView,
  DEFAULT_FILTER_BY_VIEW,
  MyConversationsFilter,
  conversationBelongsToView,
} from "@/types/conversation-filters.types";
import { resolveInboxAutoSelection } from "@/types/inbox-auto-selection";
import { ConversationDetailsParams } from "@/types/message.types";
import { PlatformsName as Platforms } from "@/types/platform.types";
import { PaginatedResponse } from "@/types/requests.types";
import { KeyedMutator } from "swr";

import useAuth from "./auth-context";

interface NotificationContextProps {
  chats: IChat[] | null;
  isLoadingChats: boolean;
  chatError: Error | undefined;
  onStartNewChat: (newChat: IChat) => Promise<void>;
  handleSendNewMessage: (conversationId: number, newMessage: IMessage) => Promise<void>;
  selectedChat: IChat | null;
  setSelectedChat: React.Dispatch<React.SetStateAction<IChat | null>>;
  unReadConversationsCount: number;
  markAllMessageAsRead: ({
    platformId,
    conversationId,
  }: ConversationDetailsParams) => Promise<void>;
  custWhatsPlatform: ICustomerPlatform | null;
  setCustWhatsPlatform: React.Dispatch<React.SetStateAction<ICustomerPlatform | null>>;
  /** platformId da conexão ativa (ex.: WhatsApp 1, Gupshup 5) para APIs de conversa */
  activeChatPlatformId: number | undefined;
  updateChatInList: (updatedChat: IChat) => Promise<void>;
  isLoadingPlatform: boolean;
  isLoadingCustWhatsPlatform: boolean;
  notificationCount: number;
  combinedCount: number;
  mutate: KeyedMutator<PaginatedResponse<IChat>[]>;
  /** Aba selecionada na lista de conversas ("Minhas Conversas" ou "Agentes de IA"). */
  conversationView: ConversationView;
  setConversationView: (view: ConversationView) => void;
  /** Filtro de status selecionado dentro da aba atual. */
  conversationFilter: ConversationFilter;
  setConversationFilter: (filter: ConversationFilter) => void;
  /** Garante a conversa na lista atual (topo) e seleciona. */
  revealChatInList: (chat: IChat) => void;
  /** Lazy load: carrega a página seguinte da aba/filtro atual. */
  loadMoreChats: () => void;
  hasMoreChats: boolean;
  isLoadingMoreChats: boolean;
  totalChatsCount: number;
}

interface NotificationProviderProps {
  children: React.ReactNode;
}

type IWebSocketMessage = IConversation & {
  message: IMessage;
};

const ATTENDANT_CALL_BIP_INTERVAL_MS = 250;
const MESSAGE_QUEUE_PROCESS_DELAY_MS = 1000;

const NotificationContext = createContext<NotificationContextProps>({} as NotificationContextProps);

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const audio = useMemo(() => {
    try {
      return new Audio("/eshop.mp3");
    } catch {
      return null;
    }
  }, []);

  const playAudio = useCallback(async () => {
    if (audio) {
      try {
        await audio.play();
      } catch {
        // ignore (e.g. autoplay blocked)
      }
    }
  }, [audio]);

  const { data: allPlatforms } = useGetAllPlatforms();
  const [hasWhatsappPlatform, setHasWhatsappPlatform] = useState(true);
  const [custWhatsPlatform, setCustWhatsPlatform] = useState<ICustomerPlatform | null>(null);
  const [selectedChat, setSelectedChat] = useState<IChat | null>(null);
  const [chats, setChats] = useState<IChat[] | null>(null);
  const [conversationView, setConversationViewState] = useState<ConversationView>(
    ConversationView.MY_CONVERSATIONS
  );
  const [conversationFilter, setConversationFilter] = useState<ConversationFilter>(
    DEFAULT_FILTER_BY_VIEW[ConversationView.MY_CONVERSATIONS]
  );
  const { getPlatformByIdentifier, updatePlatforms } = usePlatformId();
  const customerId = useGetCustomerId();
  const filter = useFilters<INotification>({ initialValue: initialNotificationFilterValues });
  const filters = filter.filters;
  const pathname = useLocation();

  const whatsappPlatform = useMemo(
    () => getPlatformByIdentifier(Platforms.WHATSAPP),
    [getPlatformByIdentifier]
  );

  // Plataforma ativa da conexão do cliente: Gupshup usa o platformId da conexão (ex.: 5), WhatsApp o da plataforma (ex.: 1).
  const activePlatformId = custWhatsPlatform?.platformId ?? whatsappPlatform?.id;
  const messageQueue = useRef<IWebSocketMessage[]>([]);
  const selectedChatRef = useRef<IChat | null>(null);
  selectedChatRef.current = selectedChat;
  const conversationFilterRef = useRef(conversationFilter);
  conversationFilterRef.current = conversationFilter;
  const conversationViewRef = useRef(conversationView);
  conversationViewRef.current = conversationView;
  let user = null;
  try {
    const authContext = useAuth();
    user = authContext?.user || null;
  } catch (error) {
    // Se não há AuthProvider, user será null
    user = null;
  }

  const audioContextRef = useRef<AudioContext | null>(null);

  const unlockAudioContext = useCallback(() => {
    if (typeof window === "undefined") return;
    const AudioContextClass =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass || audioContextRef.current) return;
    try {
      const ctx = new AudioContextClass();
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }
      audioContextRef.current = ctx;
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const events = ["click", "keydown", "touchstart"];
    const onUnlock = () => {
      unlockAudioContext();
      if (audio) {
        audio.volume = 0;
        audio
          .play()
          .then(() => {
            audio!.pause();
          })
          .catch(() => {})
          .finally(() => {
            audio!.volume = 1;
          });
      }
      events.forEach((ev) => document.removeEventListener(ev, onUnlock));
    };
    events.forEach((ev) => document.addEventListener(ev, onUnlock, { once: true }));
    return () => events.forEach((ev) => document.removeEventListener(ev, onUnlock));
  }, [user, unlockAudioContext, audio]);

  /** Duplo bip de transferência: usa elemento Audio para evitar bloqueio do Chrome ao Web Audio sem user gesture */
  const playAttendantCallBip = useCallback(() => {
    if (!audio) return;
    try {
      audio.currentTime = 0;
      audio.play().then(
        () => {
          setTimeout(() => {
            audio!.currentTime = 0;
            audio!.play().catch(() => {});
          }, ATTENDANT_CALL_BIP_INTERVAL_MS);
        },
        () => {}
      );
    } catch {
      // Silencioso: falhas de autoplay são esperadas em alguns navegadores
    }
  }, [audio]);

  const loadingData = useLoading({ loadingIconColor: "primary" });
  const { startLoading, endLoading, isLoading: isLoadingPlatform } = loadingData;

  const [params, setParams] = useState({
    platformId: whatsappPlatform?.id,
    hasWhatsappPlatform,
    onSuccess: (data: IChat[]) => {
      // Seleção já feita permanece. Com conversationId pendente na URL,
      // a primeira linha não entra no lugar da conversa pedida.
      const next = resolveInboxAutoSelection(
        data,
        readPendingConversationId(),
        selectedChatRef.current
      );
      if (!next || next === selectedChatRef.current) return;
      setSelectedChat(next);
    },
  });

  const [isLoadingCustWhatsPlatform, setIsLoadingCustWhatsPlatform] = useState<boolean>(true);

  const fetchData = useCallback(async () => {
    startLoading();
    setIsLoadingCustWhatsPlatform(true);

    try {
      if (!customerId || !allPlatforms?.items) {
        setHasWhatsappPlatform(false);
        setCustWhatsPlatform(null);
        setIsLoadingCustWhatsPlatform(false);
        return;
      }

      const custPlats = await customerPlatformService.getAll();

      const whatsBspPlat =
        allPlatforms.items.find(
          (p) => p.name === Platforms.WHATSAPP_BSP || p.name?.toUpperCase() === "GUPSHUP"
        ) ?? null;
      const whatsBspId = whatsBspPlat?.id;
      const whatsBspConnect = whatsBspPlat
        ? custPlats.find((cp) => cp.platformId === whatsBspId) || null
        : null;

      const whatsPlat = allPlatforms.items.find((p) => p.name === Platforms.WHATSAPP) ?? null;
      const whatsId = whatsPlat?.id;
      const whatsConnect = whatsPlat
        ? custPlats.find((cp) => cp.platformId === whatsId) || null
        : null;

      const bspUsable =
        whatsBspConnect && isCustomerPlatformBindingActive(whatsBspConnect)
          ? whatsBspConnect
          : null;
      const wppUsable =
        whatsConnect && isCustomerPlatformBindingActive(whatsConnect) ? whatsConnect : null;
      const connect = bspUsable ?? wppUsable;

      updatePlatforms(allPlatforms.items);
      setHasWhatsappPlatform(!!connect);
      setCustWhatsPlatform(connect);
      setIsLoadingCustWhatsPlatform(false);
    } catch (error) {
      // Handle error gracefully
      setHasWhatsappPlatform(false);
      setCustWhatsPlatform(null);
      setIsLoadingCustWhatsPlatform(false);
    } finally {
      setIsLoadingCustWhatsPlatform(false);
      endLoading();
    }
  }, [
    allPlatforms?.items,
    custWhatsPlatform?.id,
    customerId,
    endLoading,
    hasWhatsappPlatform,
    startLoading,
    updatePlatforms,
  ]);

  useEffect(() => {
    fetchData();
    return () => {
      endLoading();
    };
  }, [endLoading, fetchData]);

  // Ao entrar na tela de Conversas, refaz o fetch das plataformas do cliente para
  // pegar conexão WhatsApp recém-criada (evita precisar deslogar/logar para ver dados).
  const pathnameStr = typeof pathname?.pathname === "string" ? pathname.pathname : "";
  useEffect(() => {
    if (pathnameStr.includes("/chat")) {
      fetchData();
    }
  }, [pathnameStr, fetchData]);

  // Atualiza params com o platformId correto assim que a conexão/lista de plataformas carregar.
  useEffect(() => {
    if (!activePlatformId) return;

    setParams((prevParams) => ({
      ...prevParams,
      hasWhatsappPlatform,
      platformId: activePlatformId,
    }));
  }, [activePlatformId, hasWhatsappPlatform]);

  const resetInboxList = useCallback(() => {
    // Zera o ref na hora: o onSuccess da lista nova não pode ainda ver a
    // conversa anterior e, com id pendente na URL, não reescolhe a primeira linha.
    selectedChatRef.current = null;
    setSelectedChat(null);
    setChats(null);
  }, []);

  // Trocar de aba reseta o filtro para o default daquela aba e descarta a lista da aba anterior.
  const setConversationView = useCallback((view: ConversationView) => {
    setConversationViewState(view);
    setConversationFilter(DEFAULT_FILTER_BY_VIEW[view]);
    resetInboxList();
  }, [resetInboxList]);

  const changeConversationFilter = useCallback((filter: ConversationFilter) => {
    setConversationFilter(filter);
    resetInboxList();
  }, [resetInboxList]);

  const revealChatInList = useCallback((chat: IChat) => {
    setChats((prev) => {
      if (!prev) return [chat];
      if (prev.some((item) => item.conversationId === chat.conversationId)) return prev;
      return [chat, ...prev];
    });
    setSelectedChat(chat);
  }, []);

  const {
    data,
    isLoading: isLoadingChats,
    error: chatError,
    mutate,
    loadMore: loadMoreChats,
    hasMore: hasMoreChats,
    isLoadingMore: isLoadingMoreChats,
    totalCount: totalChatsCount,
  } = useGetAllConversations({
    ...params,
    enabled: !!user,
    view: conversationView,
    filter: conversationFilter,
  });

  const { count: openUnreadConversationsCount, mutate: mutateUnreadConversationsCount } =
    useUnreadConversationsCount({
      platformId: params.platformId,
      enabled: !!user && !!params.platformId && params.hasWhatsappPlatform,
    });

  useEffect(() => {
    if (!data) return;
    setChats((current) => {
      const selected = selectedChatRef.current;
      const staysOnThisTab =
        selected?.conversationId &&
        !selected.endDate &&
        conversationBelongsToView(selected, conversationViewRef.current);
      if (
        staysOnThisTab &&
        !data.some((chat) => chat.conversationId === selected.conversationId)
      ) {
        return [selected, ...data];
      }
      return current === data ? current : data;
    });
  }, [data]);

  // Mantém o chat selecionado em sync com a lista: quando a conversa aberta
  // recebe nova mensagem na lista (ex.: via WebSocket), atualiza a área do chat.
  useEffect(() => {
    if (!chats?.length || !selectedChat?.conversationId) return;
    const chatInList = chats.find((c) => c.conversationId === selectedChat.conversationId);
    if (!chatInList) return;
    const listMessages = chatInList.messages?.items;
    const selectedMessages = selectedChat.messages?.items;
    if (!listMessages || !selectedMessages) return;

    const selectedIds = new Set(selectedMessages.map((message) => message.id));
    const messagesToAppend = listMessages.filter(
      (message) => message?.id != null && !selectedIds.has(message.id)
    );

    if (messagesToAppend.length === 0) return;

    setSelectedChat({
      ...selectedChat,
      ...chatInList,
      messages: {
        ...selectedChat.messages,
        items: [...selectedChat.messages.items, ...messagesToAppend],
        totalCount: Math.max(
          selectedChat.messages.totalCount || 0,
          chatInList.messages.totalCount || 0,
          selectedMessages.length + messagesToAppend.length
        ),
      },
    });
  }, [chats, selectedChat?.conversationId, selectedChat?.messages.items]);

  const markAllMessageAsRead = useCallback(
    async ({ platformId, conversationId }: ConversationDetailsParams) => {
      if (!chats) return;

      await conversationService.markAllAsRead({ platformId, conversationId });
      const readDate = new Date();

      const objIndex = chats.findIndex((chat) => chat.conversationId === conversationId);

      if (objIndex === -1) return;

      const updatedArr = [...chats];
      const updatedMessages = updatedArr[objIndex].messages.items.map((message) => {
        return {
          ...message,
          readDate: readDate.toDateString(),
        };
      });

      updatedArr[objIndex] = {
        ...chats![objIndex],
        read: true,
        readDate: readDate.toString(),
        messages: {
          ...chats![objIndex].messages,
          items: updatedMessages,
        },
      };

      setChats(
        updatedArr.sort(
          (a, b) =>
            new Date(b.lastInterationDate).getTime() - new Date(a.lastInterationDate).getTime()
        )
      );

      if (selectedChat?.conversationId === conversationId) {
        setSelectedChat(updatedArr[objIndex]);
      }

      void mutateUnreadConversationsCount();
    },
    [chats, selectedChat, mutateUnreadConversationsCount]
  );

  const unReadConversationsCount = openUnreadConversationsCount;

  const { data: notificationsData } = useGetNotifications({ filters, pageNumber, pageSize });
  const notificationCount = notificationsData?.totalCount || 0;
  const combinedCount = unReadConversationsCount + notificationCount;
  // Sincronizar badge do ícone PWA com contagem de não lidas
  useEffect(() => {
    if (!isPWAStandalone()) return;
    if ('setAppBadge' in navigator) {
      if (combinedCount > 0) {
        (navigator as any).setAppBadge(combinedCount).catch(() => {});
      } else {
        (navigator as any).clearAppBadge().catch(() => {});
      }
    }
  }, [combinedCount]);

  // Registrar Periodic Background Sync para atualizar badge com app fechado
  useEffect(() => {
    if (!isPWAStandalone()) return;

    const registerPeriodicSync = async () => {
      try {
        const registration = await navigator.serviceWorker?.ready;
        if (!registration) return;

        // Enviar config para o SW
        const token = getAccessToken();
        const apiUrl = import.meta.env.VITE_API_URL || "https://api-qas.talk2buy.com.br";
        if (token && activePlatformId && customerId) {
          registration.active?.postMessage({
            type: 'BADGE_SYNC_CONFIG',
            apiUrl,
            accessToken: token,
            platformId: activePlatformId,
            customerId,
          });
        }

        // Registrar periodic sync (se suportado)
        if ('periodicSync' in registration) {
          const status = await navigator.permissions.query({
            name: 'periodic-background-sync' as any,
          });
          if (status.state === 'granted') {
            await (registration as any).periodicSync.register('badge-sync', {
              minInterval: 60 * 1000, // 1 minuto (o browser decide o intervalo real)
            });
          }
        }
      } catch {
        // Periodic sync não suportado — degrada silenciosamente
      }
    };

    registerPeriodicSync();
  }, [activePlatformId, customerId]);

  const { mutate: mutateNotifications } = useMutate();

  useEffect(() => {
    mutateNotifications(SWR_KEYS.getNotifications);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const onStartNewChat = async (newChat: IChat) => {
    if (!chats) {
      setChats([newChat]);
      setSelectedChat(newChat);
      return;
    }

    const chatIndex = chats?.findIndex((chat) => chat.contactId === newChat.contactId) ?? -1;
    if (chatIndex === -1 || undefined) {
      setChats([newChat, ...chats]);
      setSelectedChat(newChat);
      return;
    }
    setSelectedChat(chats[chatIndex]);
  };

  const updateChatsWithNewMessage = useCallback(
    async (newMessage: IWebSocketMessage): Promise<void> => {
      void mutateUnreadConversationsCount();

      if (!chats) {
        const firstChat = (await mutate())?.[0]?.items?.[0];
        const next = resolveInboxAutoSelection(
          firstChat ? [firstChat] : [],
          readPendingConversationId(),
          selectedChatRef.current
        );
        if (next && next !== selectedChatRef.current) setSelectedChat(next);
        return;
      }

      const chatExists = chats.some((c) => c.conversationId === newMessage.id);

      if (!chatExists) {
        const firstChat = (await mutate())?.[0]?.items?.[0];
        const next = resolveInboxAutoSelection(
          firstChat ? [firstChat] : [],
          readPendingConversationId(),
          selectedChatRef.current
        );
        if (next && next !== selectedChatRef.current) setSelectedChat(next);
        return;
      }

      setChats((prev) => {
        if (!prev) return prev;

        const updatedChats = prev.map((chat) => {
          if (chat.conversationId === newMessage.id) {
            const incoming = newMessage.message;
            const exists = chat.messages.items.some((m) => m.id === incoming.id);
            const items = exists
              ? chat.messages.items.map((m) => (m.id === incoming.id ? incoming : m))
              : [...chat.messages.items, incoming];
            const totalCount = exists
              ? chat.messages.totalCount
              : chat.messages.totalCount + 1;
            const isLatestMessage =
              !exists || chat.messages.items[chat.messages.items.length - 1]?.id === incoming.id;

            return {
              ...chat,
              contactId: newMessage.contactId,
              read: newMessage.read,
              rowDate: isLatestMessage
                ? new Date(newMessage.message.rowDate).toISOString()
                : chat.rowDate,
              text: isLatestMessage ? newMessage.message.text : chat.text,
              workflowName: newMessage.workflowName,
              userName: newMessage.userName,
              lastReadDate: new Date(newMessage.lastReadDate).toISOString(),
              lastInterationDate: new Date(newMessage.lastInterationDate).toISOString(),
              lastReceivedDate: newMessage.lastReceivedDate
                ? new Date(newMessage.lastReceivedDate).toISOString()
                : chat.lastReceivedDate,
              userId: newMessage.userId,
              endDate: newMessage.endDate
                ? new Date(newMessage.endDate).toISOString()
                : chat.endDate,
              messages: {
                ...chat.messages,
                items,
                totalCount,
              },
            };
          }
          return chat;
        });

        const closed = Boolean(newMessage.endDate);
        const viewingClosed =
          conversationFilterRef.current === AIAgentsFilter.CLOSED ||
          conversationFilterRef.current === MyConversationsFilter.CLOSED;
        const visibleChats = updatedChats.filter((chat) => {
          if (closed && !viewingClosed && chat.conversationId === newMessage.id) {
            return false;
          }
          return conversationBelongsToView(chat, conversationViewRef.current);
        });

        visibleChats.sort(
          (a, b) =>
            new Date(b.lastInterationDate).getTime() - new Date(a.lastInterationDate).getTime()
        );

        const currentSelectedChat = selectedChatRef.current;
        if (currentSelectedChat?.conversationId === newMessage.id) {
          if (closed && !viewingClosed) {
            setSelectedChat(null);
          } else {
            const updatedSelectedChat = visibleChats.find(
              (chat) => chat.conversationId === newMessage.id
            );
            if (updatedSelectedChat) setSelectedChat(updatedSelectedChat);
          }
        }
        return visibleChats;
      });

      if (newMessage.endDate) {
        void mutate();
      }
    },
    [chats, mutate, mutateUnreadConversationsCount]
  );

  const updateChatInList = useCallback(
    async (updatedChat: IChat): Promise<void> => {
      if (!chats) return;
      setChats(
        chats.map((chat) =>
          chat.conversationId === updatedChat.conversationId ? updatedChat : chat
        )
      );
    },
    [chats]
  );

  const updateChatWithNewSentMessage = useCallback(
    async (conversationId: number, newMessage: IMessage) => {
      if (!chats) return;

      const updatedChats = chats.map((chat) => {
        if (chat.conversationId === conversationId) {
          const exists = chat.messages.items.some((m) => m.id === newMessage.id);
          const items = exists
            ? chat.messages.items.map((m) => (m.id === newMessage.id ? newMessage : m))
            : [...chat.messages.items, newMessage];
          const totalCount = exists
            ? chat.messages.totalCount
            : chat.messages.totalCount + 1;

          return {
            ...chat,
            text: newMessage.text,
            messages: {
              ...chat.messages,
              items,
              totalCount,
            },
          };
        }
        return chat;
      });

      setChats(updatedChats);
    },
    [chats]
  );

  const handleSendNewMessage = async (conversationId: number, newMessage: IMessage) => {
    if (!selectedChat || !chats) return;

    if (selectedChat.conversationId === -1) {
      await mutate();
      return;
    }

    await updateChatWithNewSentMessage(conversationId, newMessage);
  };

  const processingQueue = useRef<Promise<void>>(Promise.resolve());
  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const processMessageQueue = useCallback(async () => {
    while (messageQueue.current.length > 0) {
      const newMessage = messageQueue.current.shift();
      if (newMessage) {
        await updateChatsWithNewMessage(newMessage);

        const isAssignedToCurrentUser =
          newMessage?.userId != null && user != null && newMessage.userId === user.id;
        const chat = chats?.find((c) => c.conversationId === newMessage.id);
        const isFromContact = chat?.contactExternalCode
          ? newMessage?.message?.fromId === chat.contactExternalCode
          : false;

        if (user?.enableSoundNotification && isAssignedToCurrentUser && isFromContact) {
          playAudio();
        }

        // Notificação nativa quando o app está em segundo plano (funciona em PWA e aba normal)
        if (document.hidden && isAssignedToCurrentUser && isFromContact) {
          const contactName = chat?.contactName || "Nova mensagem";
          const messageText = newMessage.message?.text || "Você recebeu uma nova mensagem";
          pushNotificationService.showNotification(contactName, messageText, {
            conversationId: newMessage.id,
            contactName,
            url: `/customerService/chat/${localStorage.getItem("lang") || "PT-BR"}?conversationId=${newMessage.id}`,
          });
        }

        await delay(MESSAGE_QUEUE_PROCESS_DELAY_MS);
      }
    }
  }, [playAudio, updateChatsWithNewMessage, user, chats]);

  const receiveMessageCallback = useCallback(
    async (data: string) => {
      const deserializedData = JSON.parse(data) as IWebSocketMessage;
      messageQueue.current.push(deserializedData);
      processingQueue.current = processingQueue.current.then(() => processMessageQueue());
    },
    [processMessageQueue]
  );

  const receiveAttendantCallCallback = useCallback(
    (_data: string) => {
      if (import.meta.env.DEV) {
        console.log(
          "[NotificationSound] Transferência de atendimento recebida - bip tocado (independente de enableSoundNotification)"
        );
      }
      playAttendantCallBip();
    },
    [playAttendantCallBip]
  );

  const receiveMessageHandlerRef = useRef(receiveMessageCallback);
  const receiveAttendantCallHandlerRef = useRef(receiveAttendantCallCallback);
  receiveMessageHandlerRef.current = receiveMessageCallback;
  receiveAttendantCallHandlerRef.current = receiveAttendantCallCallback;

  const stableReceiveMessage = useCallback((data: string) => {
    markUserActivity();
    receiveMessageHandlerRef.current(data);
  }, []);

  const stableReceiveAttendantCall = useCallback((data: string) => {
    receiveAttendantCallHandlerRef.current(data);
  }, []);

  const connectSignalR = useCallback(async () => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) return;

      const refreshAndStartConnection = async (refreshToken: string) => {
        try {
          const { accessToken: newAccessToken } = await authService.refreshToken(refreshToken);
          signalRService.startConnection({ accessToken: newAccessToken });
        } catch {
          // Se falhar ao renovar token, não conectar SignalR
        }
      };

      const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);

      if (checkIfTokenIsExpired(decodeToken(accessToken)) && refreshToken) {
        await refreshAndStartConnection(refreshToken);
        return;
      }

      signalRService.startConnection({ accessToken });
    } catch {
      // Se não há token ou usuário autenticado, não conectar SignalR
    }
  }, []);

  useEffect(() => {
    // Só conectar SignalR se houver usuário autenticado
    if (user) {
      connectSignalR();
    }
  }, [connectSignalR, user]);

  // Reconectar SignalR agressivamente ao retornar do segundo plano
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && user) {
        const token = getAccessToken();
        if (token && signalRService.connection?.state !== "Connected") {
          signalRService.startConnection({ accessToken: token });
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    signalRService.on("receiveMessage", stableReceiveMessage);
    signalRService.on("receiveAttendantCall", stableReceiveAttendantCall);
    return () => {
      signalRService.off("receiveMessage", stableReceiveMessage);
      signalRService.off("receiveAttendantCall", stableReceiveAttendantCall);
    };
  }, [user?.id, stableReceiveMessage, stableReceiveAttendantCall]);

  return (
    <NotificationContext.Provider
      value={{
        chats,
        isLoadingChats,
        chatError,
        onStartNewChat,
        handleSendNewMessage,
        selectedChat,
        setSelectedChat,
        unReadConversationsCount,
        markAllMessageAsRead,
        custWhatsPlatform,
        setCustWhatsPlatform,
        activeChatPlatformId: activePlatformId,
        updateChatInList,
        isLoadingPlatform,
        isLoadingCustWhatsPlatform,
        notificationCount,
        combinedCount,
        mutate,
        conversationView,
        setConversationView,
        conversationFilter,
        setConversationFilter: changeConversationFilter,
        revealChatInList,
        loadMoreChats,
        hasMoreChats,
        isLoadingMoreChats,
        totalChatsCount,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);
